import { FEATURES } from '../../../../lib/config/constants.js'
import { isDrayNowHost } from '../../../../lib/config/multitenancy.js'

/**
 * Filter Definitions for Marketplace
 *
 * Defines all available filters, their types, roles, and default values.
 */

/**
 * Filter types enum
 */
export const FILTER_TYPES = {
  DATE: 'date',
  IMC: 'imc',
  CARRIER: 'carrier',
  DRIVER: 'driver',
  EDI: 'edi',
  STREET_TURNS: 'streetTurns',
  CREATED_BY: 'createdBy',
  LANE_LABEL: 'laneLabel',
  FREIGHT_TYPE: 'freightType',
  PENDING_DRIVER_ACCEPTANCE: 'pendingDriverAcceptance',
}

/**
 * User roles for filter visibility
 */
export const FILTER_ROLES = {
  ADMIN: 'admin',
  IMC: 'imc',
  CARRIER: 'carrier',
}

/**
 * All filter definitions
 * Each filter has:
 * - key: unique identifier
 * - name: display name
 * - roles: array of roles that can see this filter
 * - component: filter component type
 * - multiSelect: whether multiple values can be selected
 * - featureFlag: optional feature flag requirement
 * - defaultValue: default value when selected
 */
export const FILTER_DEFINITIONS = [
  {
    key: FILTER_TYPES.DATE,
    name: 'Date',
    roles: [FILTER_ROLES.ADMIN, FILTER_ROLES.IMC],
    component: 'DateRangeFilter',
    multiSelect: false,
    defaultValue: '',
  },
  {
    key: FILTER_TYPES.IMC,
    name: 'IMC',
    roles: [FILTER_ROLES.ADMIN],
    component: 'CompanySelectFilter',
    multiSelect: true,
    companyType: 'imc',
    defaultValue: [],
  },
  {
    key: FILTER_TYPES.CARRIER,
    name: 'Carrier',
    roles: [FILTER_ROLES.ADMIN, ...(isDrayNowHost() ? [] : [FILTER_ROLES.IMC])],
    component: 'CompanySelectFilter',
    multiSelect: false,
    companyType: 'carrier',
    defaultValue: null,
  },
  {
    key: FILTER_TYPES.DRIVER,
    name: 'Driver',
    roles: [FILTER_ROLES.ADMIN],
    component: 'UserSelectFilter',
    multiSelect: false,
    userRole: 'driver',
    defaultValue: null,
  },
  {
    key: FILTER_TYPES.EDI,
    name: 'EDI',
    roles: [FILTER_ROLES.ADMIN],
    component: 'CheckboxFilter',
    multiSelect: false,
    defaultValue: true,
    checkboxLabel: 'FROM EDI',
    uncheckedLabel: 'NOT FROM EDI',
  },
  {
    key: FILTER_TYPES.STREET_TURNS,
    name: 'Street Turns',
    roles: [FILTER_ROLES.ADMIN],
    component: 'CheckboxGroupFilter',
    multiSelect: false,
    featureFlag: FEATURES.STREET_TURN_MANAGEMENT,
    defaultValue: {
      bundledStreetTurns: true,
      interchanges: true,
      ordersInBundle: true,
    },
    options: [
      {
        key: 'bundledStreetTurns',
        label: 'Bundled Street Turns',
        testId: 'marketplace-filters-bundled-street-turns-checkbox',
      },
      {
        key: 'interchanges',
        label: 'Interchanges',
        testId: 'marketplace-filters-interchanges-checkbox',
      },
      {
        key: 'ordersInBundle',
        label: 'Orders in Bundles',
        testId: 'marketplace-filters-orders-in-bundles-checkbox',
      },
    ],
  },
  {
    key: FILTER_TYPES.CREATED_BY,
    name: 'Created By',
    roles: [FILTER_ROLES.ADMIN, FILTER_ROLES.IMC],
    component: 'UserSelectFilter',
    multiSelect: true,
    userRole: null,
    hasCreatedOrders: true,
    defaultValue: [],
  },
  {
    key: FILTER_TYPES.LANE_LABEL,
    name: 'Lane Label',
    roles: [FILTER_ROLES.ADMIN],
    component: 'CheckboxGroupFilter',
    multiSelect: false,
    featureFlag: FEATURES.LANES,
    defaultValue: {
      laneAwarded: true,
      laneConsistent: true,
      laneNoLabel: true,
    },
    options: [
      {
        key: 'laneAwarded',
        label: 'Awarded Lane',
        testId: 'marketplace-filters-awarded-lane-checkbox',
      },
      {
        key: 'laneConsistent',
        label: 'Consistent',
        testId: 'marketplace-filters-consistent-checkbox',
      },
      {
        key: 'laneNoLabel',
        label: 'Not Set',
        testId: 'marketplace-filters-not-set-checkbox',
      },
    ],
  },
  {
    key: FILTER_TYPES.FREIGHT_TYPE,
    name: 'Freight Type',
    roles: [FILTER_ROLES.ADMIN, FILTER_ROLES.IMC],
    component: 'FreightTypeFilter',
    multiSelect: false,
    featureFlag: FEATURES.FREIGHT_TYPE_LOCATION_BY_PLACE_ID,
    defaultValue: null,
  },
  {
    key: FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE,
    name: 'Pending Driver Acceptance',
    roles: [FILTER_ROLES.ADMIN, FILTER_ROLES.CARRIER],
    component: 'CheckboxFilter',
    multiSelect: false,
    defaultValue: true,
    checkboxLabel: 'TENDERED ORDERS',
    uncheckedLabel: 'TENDERED ORDERS',
    isDrayNowHidden: true,
  },
]

/**
 * Get filters available for a given user role
 */
export const getFiltersForRole = (role, tenant = null, listType = 'orders') => {
  return FILTER_DEFINITIONS.filter((filter) => {
    // Hide ModalView-only filters on DrayNow
    if (filter.isDrayNowHidden && isDrayNowHost()) {
      return false
    }

    // Check role access
    if (!filter.roles.includes(role)) {
      // Check if carrier has access via feature flag
      if (role === FILTER_ROLES.CARRIER) {
        const carrierFilters =
          tenant?.features?.[FEATURES.AVAILABLE_FILTERING_FIELDS.name]?.enabled
        if (!carrierFilters || !Array.isArray(carrierFilters)) return false
        // Check if this specific filter is in the enabled list (case-insensitive)
        const enabledKeys = carrierFilters.map((f) => f.toLowerCase())
        if (!enabledKeys.includes(filter.key.toLowerCase())) {
          return false
        }
      } else {
        return false
      }
    }

    // Check feature flag if required
    if (filter.featureFlag && tenant) {
      if (!tenant.isFeatureEnabled?.(filter.featureFlag)) {
        return false
      }
    }

    // Exclude IMC filter for trips — but not for carriers, who only ever see
    // the trips list and keep IMC when it is in their whitelist (legacy
    // `MarketplaceFilters.js` gated this on `!isUserCarrier(market)`).
    if (
      listType === 'trips' &&
      filter.key === FILTER_TYPES.IMC &&
      role !== FILTER_ROLES.CARRIER
    ) {
      return false
    }

    return true
  })
}

/**
 * Get filter definition by key
 */
export const getFilterByKey = (key) => {
  return FILTER_DEFINITIONS.find((f) => f.key === key)
}

/**
 * Format filter options for Semantic UI dropdown
 */
export const getFilterDropdownOptions = (filters) => {
  return filters.map((filter, index) => ({
    key: index,
    value: filter.key,
    text: filter.name,
  }))
}
